import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as api from '../api/api';
import { goalValueToDisplay, goalValueToStored, goalUnitLabel, formatGoalValue, summarizeGoalColumnTotals, columnLabel } from '../utils/GoalValueUtil';
import './Plan.css';

const formatShortDate = (isoDate) => new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
const currentYear = new Date().getFullYear();
const todayIso = new Date().toISOString().slice(0, 10);

const PlanYearView = () => {
    const { year: yearParam } = useParams();
    const navigate = useNavigate();
    const year = Number(yearParam) || currentYear;

    const [plan, setPlan] = useState(null);
    const [unitSettings, setUnitSettings] = useState({ distance_unit: 'KILOMETRES', elevation_unit: 'METRES' });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const currentWeekRowRef = useRef(null);
    const hasScrolledToCurrentWeekRef = useRef(false);

    useEffect(() => {
        // Falls back to the hardcoded KILOMETRES/METRES defaults already in
        // state on failure - unit labels being briefly wrong is better than
        // this failing silently with no indication settings didn't load.
        api.getUserSettings().catch((e) => {
            console.error('Failed to load unit settings:', api.getErrorMessage(e, e));
        }).then((settings) => settings && setUnitSettings(settings));
    }, []);

    useEffect(() => {
        hasScrolledToCurrentWeekRef.current = false;
        setIsLoading(true);
        setError('');
        api.getYearPlan(year)
            .then(({ data }) => setPlan(data))
            .catch((e) => setError(api.getErrorMessage(e, 'Unable to load this year\'s plan.')))
            .finally(() => setIsLoading(false));
    }, [year]);

    // Jump straight to this week on first load of a year's page — saving a
    // goal value later re-renders the table too, but shouldn't re-trigger
    // the scroll, hence the once-per-year-load guard.
    useEffect(() => {
        if (!isLoading && plan && !hasScrolledToCurrentWeekRef.current && currentWeekRowRef.current) {
            currentWeekRowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
            hasScrolledToCurrentWeekRef.current = true;
        }
    }, [isLoading, plan]);

    const saveWeekField = (weekIndex, updatedFields) => {
        const nextWeeks = plan.weeks.map((week, index) => index === weekIndex ? { ...week, ...updatedFields } : week);
        const nextPlan = { ...plan, weeks: nextWeeks };
        setPlan(nextPlan);
        const week = nextWeeks[weekIndex];
        api.updateWeekPlan(week.weekStartDate, {
            event: week.event,
            notes: week.notes,
            diary: week.diary,
        }).catch((e) => setError(api.getErrorMessage(e, 'Unable to save changes.')));
    };

    const saveWeekGoalValue = (weekIndex, goalId, plannedValue) => {
        const week = plan.weeks[weekIndex];
        // Re-fetch the whole year rather than splicing the single updated
        // goal value into state: the save response only carries that one
        // week's planned/actual value, not its projected value, and the
        // year total's Planned/Projected figure depends on every week's
        // projected value being present and current.
        api.updateWeekGoalValue(week.weekStartDate, goalId, plannedValue)
            .then(() => api.getYearPlan(year))
            .then(({ data }) => setPlan(data))
            .catch((e) => setError(api.getErrorMessage(e, 'Unable to save changes.')));
    };

    if (isLoading) return <main className="plan-page"><p className="plan-message">Loading year plan...</p></main>;
    if (error && !plan) return <main className="plan-page"><p className="plan-message">{error}</p></main>;
    if (!plan) return null;

    // Nothing left to project once every week on the page is already
    // finished (viewing a fully past year) — the total row's label reflects
    // that instead of calling a settled number "Projected".
    const isYearFullyPast = plan.weeks.every((week) => week.weekEndDate < todayIso);

    return (
        <main className="plan-page">
            <header className="plan-heading">
                <div><span className="eyebrow">Year plan</span><h1>{year}</h1></div>
                <div className="plan-week-nav">
                    <button type="button" onClick={() => navigate(`/plan/year/${year - 1}`)}>‹ {year - 1}</button>
                    <button type="button" onClick={() => navigate(`/plan/year/${currentYear}`)} disabled={year === currentYear}>This year</button>
                    <button type="button" onClick={() => navigate(`/plan/year/${year + 1}`)}>{year + 1} ›</button>
                    <Link to="/plan" className="plan-year-link">This week ›</Link>
                </div>
            </header>

            <div className="plan-table-wrap">
                <table className="plan-table plan-year-table">
                    <thead>
                        <tr>
                            <th>Week of</th>
                            {plan.columns.map((column) => <th key={column.columnKey}>{columnLabel(column)} ({goalUnitLabel(column.goalUnit, unitSettings)})</th>)}
                            <th>Event</th><th>Notes</th><th>Diary</th>
                        </tr>
                    </thead>
                    <tbody>
                        {plan.weeks.map((week, index) => (
                            <tr
                                key={week.weekStartDate}
                                ref={week.isCurrentWeek ? currentWeekRowRef : undefined}
                                className={week.isCurrentWeek ? 'plan-row-today' : week.weekEndDate < todayIso ? 'plan-row-past' : ''}
                            >
                                <td className="plan-day-cell">
                                    {/* Same button as the week view's day cell:
                                        the row's own date is the way down into
                                        that week's detail. */}
                                    <Link
                                        to={`/plan/week/${week.boundaryWeekStartDate || week.weekStartDate}`}
                                        className="plan-jump-button"
                                        title={`Open the week of ${formatShortDate(week.weekStartDate)}`}
                                    >
                                        <span className="plan-jump-button-main">{formatShortDate(week.weekStartDate)}</span>
                                        <span className="plan-jump-button-sub">Week plan</span>
                                        <span className="plan-jump-button-chevron" aria-hidden="true">›</span>
                                    </Link>
                                    {week.isCurrentWeek && <small>this week</small>}
                                    {week.isPartial && <small>partial week</small>}
                                </td>
                                {plan.columns.map((column) => {
                                    const goalValue = week.goalValues.find((value) => value.columnKey === column.columnKey);
                                    if (!goalValue) return <td key={column.columnKey} className="plan-cell-inactive" />;
                                    return (
                                        <td key={column.columnKey} className="plan-goal-cell">
                                            <div className="plan-goal-subfield">
                                                <small>Plan</small>
                                                {goalValue.isEditable === false
                                                    ? <span className="plan-goal-value-static">{formatGoalValue(goalValue.plannedValue, column.goalUnit, unitSettings)}</span>
                                                    : <span className="plan-input-with-unit">
                                                        <input
                                                            key={`${column.columnKey}-${goalValue.goalId}`}
                                                            type="number"
                                                            className="plan-value-input"
                                                            step="0.1"
                                                            min="0"
                                                            defaultValue={goalValueToDisplay(goalValue.plannedValue, column.goalUnit, unitSettings) ?? ''}
                                                            onBlur={(event) => saveWeekGoalValue(index, goalValue.goalId, goalValueToStored(event.target.value, column.goalUnit, unitSettings))}
                                                        />
                                                        <span className="plan-input-unit">{goalUnitLabel(column.goalUnit, unitSettings)}</span>
                                                    </span>}
                                            </div>
                                            <div className="plan-goal-subfield plan-goal-actual">
                                                <small>Actual</small>
                                                <span>{formatGoalValue(goalValue.actualValue, column.goalUnit, unitSettings)}</span>
                                            </div>
                                        </td>
                                    );
                                })}
                                <td><input type="text" defaultValue={week.event || ''} onBlur={(event) => saveWeekField(index, { event: event.target.value })} /></td>
                                <td><input type="text" defaultValue={week.notes || ''} onBlur={(event) => saveWeekField(index, { notes: event.target.value })} /></td>
                                <td><input type="text" defaultValue={week.diary || ''} onBlur={(event) => saveWeekField(index, { diary: event.target.value })} /></td>
                            </tr>
                        ))}
                        <tr className="plan-total-row">
                            <td>Total for year</td>
                            {plan.columns.map((column) => {
                                const columnGoalValues = plan.weeks.flatMap((week) => week.goalValues.filter((value) => value.columnKey === column.columnKey));
                                // The year's total is a projection: what's actually
                                // been done on the days before today, plus whichever
                                // of actual/plan is bigger for today, plus what's still
                                // planned for the days and weeks still to come. The
                                // backend computes this per week (see projectedValue).
                                // Once every week on the page has already finished (a
                                // fully past year), there's nothing left to project —
                                // so it shows what was planned instead.
                                const planOrProjectedValues = columnGoalValues.map((value) => (
                                    isYearFullyPast ? value.plannedValue : value.projectedValue
                                ));
                                const summary = summarizeGoalColumnTotals(
                                    planOrProjectedValues,
                                    columnGoalValues.map((value) => value.actualValue),
                                    column.targetValue,
                                    column.goalUnit,
                                    unitSettings,
                                );
                                return (
                                    <td key={column.columnKey} className="plan-goal-cell">
                                        <div className="plan-goal-subfield"><small>Target{summary.targetPercent ? ` (${summary.targetPercent})` : ''}</small><span>{summary.target}</span></div>
                                        <div className="plan-goal-subfield"><small>{isYearFullyPast ? 'Planned' : 'Projected'}</small><span>{summary.plan}</span></div>
                                        <div className="plan-goal-subfield plan-goal-actual"><small>Actual</small><span>{summary.actual}</span></div>
                                    </td>
                                );
                            })}
                            <td /><td /><td />
                        </tr>
                    </tbody>
                </table>
            </div>
            {error && <p className="plan-message plan-error">{error}</p>}
        </main>
    );
};

export default PlanYearView;
